import { ProjectState, SurfaceId, TileCategory, TileData } from './types';
import { DEFAULT_FRIEZE_MIX } from './layouts/friezeMixes';

const colors: Record<TileCategory, string> = {
  floor: '#9b9286',
  darkWall: '#35414a',
  lightWall: '#d8d3cb',
  decor: '#7f947f',
  border: '#b58c58',
};

const makeTexture = (category: TileCategory, label: string): string => {
  const color = colors[category];
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="800" height="800"><defs><filter id="n"><feTurbulence baseFrequency=".012" numOctaves="3" seed="${category.length}"/><feBlend mode="soft-light" in2="SourceGraphic"/></filter></defs><rect width="100%" height="100%" fill="${color}"/><rect width="100%" height="100%" filter="url(#n)" opacity=".32"/><path d="M-80 520C180 370 410 650 900 330" fill="none" stroke="white" stroke-opacity=".13" stroke-width="18"/><text x="50%" y="52%" text-anchor="middle" font-family="Arial" font-size="52" fill="white" fill-opacity=".22">${label}</text></svg>`;
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
};

export const createTile = (
  category: TileCategory,
  name: string,
  width: number,
  height: number,
  price: number,
): TileData => ({
  id: `demo-${category}`,
  category,
  name,
  sku: `KL-${category.toUpperCase().slice(0, 3)}-01`,
  imagePath: makeTexture(category, name),
  originalWidth: 800,
  originalHeight: 800,
  tileWidth: width,
  tileHeight: height,
  groutWidth: 3,
  rotation: 0,
  scale: 1,
  mirror: false,
  variationEnabled: true,
  price,
});

const surfaceSettings = (templateId: string) => ({
  templateId,
  alignment: 'center' as const,
  customOffset: 0,
  minCutSize: 80,
  symmetry: true,
  centerPattern: true,
  friezeMix: DEFAULT_FRIEZE_MIX,
});

export const createDefaultProject = (): ProjectState => {
  const surfaces: Record<SurfaceId, ReturnType<typeof surfaceSettings>> = {
    front: surfaceSettings('classic-dark-light'),
    left: surfaceSettings('wall-straight'),
    right: surfaceSettings('classic-dark-light'),
    back: surfaceSettings('decor-center'),
    floor: surfaceSettings('floor-straight'),
  };

  return {
    id: crypto.randomUUID(),
    name: 'Ванная комната',
    room: { width: 3600, length: 2400, height: 2700 },
    tiles: {
      floor: createTile('floor', 'Stone Graphite', 600, 600, 2390),
      darkWall: createTile('darkWall', 'Slate Night', 300, 600, 1890),
      lightWall: createTile('lightWall', 'Linen Pearl', 300, 600, 1690),
      decor: createTile('decor', 'Botanic Moss', 300, 600, 2590),
      border: createTile('border', 'Brass Line', 600, 100, 790),
    },
    surfaces,
    selectedSurface: 'front',
    selectedTile: 'lightWall',
    grout: { width: 3, color: '#d7d2ca', finish: 'matte' },
    lighting: {
      brightness: 106,
      intensity: 78,
      temperature: 4800,
      direction: 'left',
      softness: 68,
      preset: 'day',
    },
    ceiling: { color: '#f4f2ee', glossy: true, spotlights: 4 },
    objects: [
      { id: crypto.randomUUID(), type: 'door', surfaceId: 'right', width: 800, height: 2050, x: 180, y: 0, swing: 'right' },
      { id: crypto.randomUUID(), type: 'window', surfaceId: 'front', width: 700, height: 600, x: 2250, y: 1250 },
    ],
    fixtures: [
      { id: crypto.randomUUID(), kind: 'bath', x: 500, z: 1800, rotation: 0 },
    ],
    materials: {
      tileThickness: 9,
      glueBagSize: 25,
      groutPackSize: 5,
      gluePricePerKg: 42,
      groutPricePerKg: 190,
      reservePercent: 10,
      tilesPerPack: {},
    },
    wastePercent: 10,
    viewMode: 'room',
    presetId: undefined,
    updatedAt: new Date().toISOString(),
  };
};
